import React, { useMemo, useRef, useState } from "react";
import { doc, setDoc, serverTimestamp, runTransaction, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import {
  FaMinus, FaPlus, FaCopy, FaFilePdf, FaCheck,
  FaRegBuilding, FaStore, FaUserTie, FaBroom, FaSwimmingPool, FaArrowsAltV, FaCar, FaRegClock,
  FaAddressCard, FaTimes, FaTrash, FaMagic,
} from "react-icons/fa";
import {
  colors, radius, spacing, typography, cardBase, inputBase,
  btnPrimary, btnSecondary,
} from "../../utils/designTokens";
import {
  TARIFA_RAPIDA, cotizacionRapida, configDesdeCotizacion, defaultConfig, fmt,
} from "../../utils/presupuestoCalc";
import {
  radiografiaDeCatastro, personalizarHeuristico, payloadPersonalizacion, PERFILES_PROPIETARIOS,
} from "../../utils/presupuestoPersonalizacion";
import { generarPropuestaIA } from "../../utils/habiApi";
import { generarPresupuestoPdf } from "../../utils/presupuestoPdf";
import PresupuestoTemplate from "../../components/presupuesto/PresupuestoTemplate";
import AppSelect from "../../components/AppSelect";
import { useIsMobile } from "../../hooks/useResponsive";
import { useToast } from "../../components/Toast";
import { useUser } from "../../context/UserContext";
import { useCotizadorPrefill } from "../../context/CotizadorContext";
import { useProspeccionData } from "../../hooks/useProspeccionData";
import { computeBloqueadoHasta, estaLibre, puedeEditar, fmtFechaLiberacion } from "../../utils/crmAsignacion";

/**
 * Cotizador rapido del comercial: carga cantidades (apartamentos, portero,
 * limpieza, amenities, ascensores) y cotiza en vivo con la tarifa escalonada
 * de TARIFA_RAPIDA. Desde aca mismo copia el resumen para WhatsApp o descarga
 * el presupuesto PDF completo (mismo template de 6 paginas que Presupuestos).
 */
export default function Cotizador() {
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { userType } = useUser();
  // Prefill desde la ficha CRM ("Cotizar honorario"): el componente se monta de
  // cero en cada cambio de pantalla, así que alcanza con los inicializadores.
  // El prefill de una administración es del Cotizador Back office: acá se ignora.
  const { prefill: prefillCtx, clearPrefill } = useCotizadorPrefill();
  const prefill = prefillCtx?.origen === "administracion" ? null : prefillCtx;
  const { crmByPadron, comerciales, updateCrm } = useProspeccionData();
  const [edificio, setEdificio] = useState(prefill?.edificio || "");
  const [ubicacion, setUbicacion] = useState(prefill?.ubicacion || "");
  const [inputs, setInputs] = useState({
    apartamentos: prefill ? (Number(prefill.apartamentos) || 0) : 20,
    locales: 0, porteros: 0, limpiadoras: 0, amenities: 0, ascensores: 0,
    garages: 0, antiguedadAnios: prefill ? (Number(prefill.antiguedadAnios) || 0) : 0,
    aplicaPromo: true, promoPorcentaje: 50, promoMeses: 3,
  });
  const [copiado, setCopiado] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [savingProspecto, setSavingProspecto] = useState(false);
  const [marcarPropuesta, setMarcarPropuesta] = useState(true);
  const [registrado, setRegistrado] = useState(null); // {nro, monto} ya guardado — evita notas duplicadas
  const templateRef = useRef(null);
  // Nro de propuesta y fecha estables durante la sesion de cotizacion.
  const [baseCfg] = useState(() => defaultConfig());

  // ── Personalización de la propuesta (radiografía de catastro + prosa IA) ──
  // Activa por defecto cuando se cotiza desde una ficha (ya hay datos de catastro).
  const [personalizar, setPersonalizar] = useState(!!prefill);
  const [perso, setPerso] = useState(() => ({
    ciudad: prefill?.ciudad || "Montevideo",
    jurisdiccion: prefill?.jurisdiccion || prefill?.ciudad || "Montevideo",
    clienteNombre: prefill?.clienteNombre || (prefill?.direccion ? `Comisión · ${prefill.direccion}` : ""),
    zona: prefill?.barrio || "",
    perfil: "mixto",
    contexto: "",
    radUnidades: prefill?.apartamentos || "",
    radAnio: prefill?.anioConstruccion || "",
    radPlantas: "",
    radM2: prefill?.m2 || "",
    radRegimen: prefill?.regimen || "",
    notaRadiografia: "",
  }));
  const [secciones, setSecciones] = useState([]); // [{titulo, cuerpo}] editables
  const [leads, setLeads] = useState({ cover: "", precio: "" });
  const [genIA, setGenIA] = useState(false);
  const setP = (k, v) => setPerso((p) => ({ ...p, [k]: v }));

  const radiografia = useMemo(() => radiografiaDeCatastro({
    unidades: perso.radUnidades, anio: perso.radAnio, plantas: perso.radPlantas,
    m2: perso.radM2, zona: perso.zona, regimen: perso.radRegimen,
  }), [perso]);

  const crmActual = prefill ? crmByPadron[String(prefill.padron)] || null : null;
  const estadoActual = crmActual?.estado || "sin_contactar";
  const prePropuesta = ["sin_contactar", "contactado", "en_conversacion", "reunion"].includes(estadoActual);

  const q = useMemo(() => cotizacionRapida(inputs), [inputs]);
  const pdfConfig = useMemo(() => {
    const overrides = {
      ...baseCfg,
      edificio: edificio.trim() || "Tu edificio",
      ubicacion: ubicacion.trim() || "Montevideo",
    };
    if (personalizar) {
      overrides.ciudad = perso.ciudad || "Montevideo";
      overrides.jurisdiccion = perso.jurisdiccion || perso.ciudad || "Montevideo";
      if (perso.clienteNombre.trim()) overrides.clienteNombre = perso.clienteNombre.trim();
      if (leads.cover.trim()) overrides.coverLead = leads.cover.trim();
      if (leads.precio.trim()) overrides.precioLead = leads.precio.trim();
      overrides.radiografia = radiografia.length ? radiografia : null;
      if (perso.notaRadiografia.trim()) overrides.notaRadiografia = perso.notaRadiografia.trim();
      const secs = secciones.filter((s) => (s.titulo || "").trim() || (s.cuerpo || "").trim());
      overrides.personalizado = secs.length ? secs : null;
    }
    return configDesdeCotizacion(inputs, overrides);
  }, [inputs, edificio, ubicacion, baseCfg, personalizar, perso, radiografia, secciones, leads]);

  // Genera los textos personalizados con IA (Habi); si el backend no está,
  // cae al borrador heurístico local. Ambos devuelven { coverLead, precioLead, secciones }.
  async function generarTextos() {
    if (genIA) return;
    setGenIA(true);
    const base = {
      edificio: edificio.trim() || perso.clienteNombre.trim() || "el edificio",
      ciudad: perso.ciudad, zona: perso.zona, perfil: perso.perfil,
      servicios: { portero: inputs.porteros > 0, amenities: inputs.amenities > 0, garages: inputs.garages > 0, local: inputs.locales > 0 },
      anio: perso.radAnio || (inputs.antiguedadAnios ? new Date().getFullYear() - Number(inputs.antiguedadAnios) : null),
      unidades: perso.radUnidades || inputs.apartamentos, m2: perso.radM2, regimen: perso.radRegimen,
      contexto: perso.contexto,
    };
    const aplicar = (r) => {
      setSecciones((r.secciones || []).map((s) => ({ titulo: s.titulo || "", cuerpo: s.cuerpo || "" })));
      setLeads({ cover: r.coverLead || "", precio: r.precioLead || "" });
    };
    try {
      aplicar(await generarPropuestaIA(payloadPersonalizacion(base)));
      showToast("Textos generados con IA. Revisalos y ajustá lo que quieras.", "success");
    } catch (e) {
      aplicar(personalizarHeuristico(base));
      showToast("La IA no respondió; dejé un borrador para que edites.", "success");
    }
    setGenIA(false);
  }
  const updSeccion = (i, k, v) => setSecciones((ss) => ss.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const addSeccion = () => setSecciones((ss) => [...ss, { titulo: "", cuerpo: "" }]);
  const delSeccion = (i) => setSecciones((ss) => ss.filter((_, j) => j !== i));

  const setCant = (key, value) => setInputs((prev) => ({ ...prev, [key]: Math.max(0, Math.floor(Number(value) || 0)) }));
  const sinDatos = q.cantidades.apartamentos === 0;

  const resumenTexto = () => {
    const c = q.cantidades;
    const lineas = [
      `*Cotización Habitia*${edificio.trim() ? ` — ${edificio.trim()}` : ""}`,
      `${c.apartamentos} apartamento${c.apartamentos === 1 ? "" : "s"}${c.locales ? ` · ${c.locales} local${c.locales === 1 ? "" : "es"}` : ""}`,
    ];
    const extras = [];
    if (c.porteros) extras.push(`Portero: ${c.porteros}`);
    if (c.limpiadoras) extras.push(`Limpieza: ${c.limpiadoras}`);
    if (c.amenities) extras.push(`Amenities: ${c.amenities}`);
    if (c.ascensores) extras.push(`Ascensores: ${c.ascensores}`);
    if (c.garages) extras.push("Garage");
    if (extras.length) lineas.push(extras.join(" · "));
    if (q.antiguedadAplica) lineas.push(`Antigüedad: ${q.antiguedadAnios} años (+10% sobre el total)`);
    lineas.push("");
    lineas.push(`Honorario mensual: ${fmt(q.totalConIva)} IVA incluido`);
    if (c.apartamentos > 0) lineas.push(`Equivale a ${fmt(q.totalPorApartamentoConIva)} por apartamento (IVA inc.)`);
    if (q.aplicaPromo && q.promoPorcentaje > 0) {
      lineas.push(`Promo de bienvenida: ${q.promoPorcentaje}% OFF los primeros ${q.promoMeses} ${q.promoMeses === 1 ? "mes" : "meses"} → ${fmt(q.promoTotalConIva)}/mes`);
    }
    lineas.push("");
    lineas.push("Incluye plataforma 24/7, app móvil y asistente IA. Onboarding bonificado.");
    return lineas.join("\n");
  };

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(resumenTexto());
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
      // Cotizar para un prospecto = queda registrado y asignado a quien cotiza.
      if (prefill && !sinDatos) await registrarEnProspecto({ cambiarEstado: false });
    } catch (err) {
      console.error("No se pudo copiar:", err);
    }
  };

  // Registra el presupuesto en el prospecto del CRM y ASIGNA el edificio a quien
  // cotiza (pedido de campo: "si cotizo, el lead queda mío" — sin paso extra).
  // Se dispara al descargar el PDF, al copiar el resumen y con el botón explícito
  // (que además puede pasar el estado a "Propuesta enviada"). Cuenta como GESTIÓN:
  // refresca el reloj de 60 días; el auto-reclamo usa la misma transacción
  // anti-concurrencia de CrmFicha. También sincroniza como corrección de campo
  // (datosCorregidos) los datos del edificio editados al cotizar.
  const registrarEnProspecto = async ({ cambiarEstado }) => {
    if (!prefill || sinDatos) return false;
    const emailUsr = auth.currentUser?.email || "comercial";
    const emailLc = emailUsr.toLowerCase();
    const isSuperAdmin = Number(userType) === 2;
    if (!puedeEditar(crmActual, { email: emailLc, userType })) {
      showToast(`Ojo: este edificio está asignado a ${crmActual?.asignadoA || "otro agente"} hasta el ${fmtFechaLiberacion(crmActual) || "—"} — el presupuesto no se registró en el CRM.`, "error");
      return false;
    }
    try {
      const nowTs = Timestamp.now();
      const miNombre = comerciales.find((c) => c.email === emailLc)?.name || emailUsr;
      const mismaCotizacion = registrado && registrado.nro === pdfConfig.propuestaNro && registrado.monto === q.totalConIva;

      // Datos del edificio editados al cotizar → corrección de campo del prospecto.
      const datosCorregidos = { ...(crmActual?.datosCorregidos || {}) };
      let huboCorreccion = false;
      if (Number(inputs.apartamentos) !== (Number(prefill.apartamentos) || 0)) {
        datosCorregidos.nUnidades = Number(inputs.apartamentos);
        huboCorreccion = true;
      }
      if (Number(inputs.antiguedadAnios) !== (Number(prefill.antiguedadAnios) || 0)) {
        datosCorregidos.anioConstruccion = new Date().getFullYear() - Number(inputs.antiguedadAnios || 0);
        huboCorreccion = true;
      }

      const payload = {
        padron: Number(prefill.padron),
        presupuestoMonto: q.totalConIva,
        presupuestoNro: pdfConfig.propuestaNro,
        presupuestoAt: nowTs,
        presupuestoInputs: { ...inputs },
        direccion: prefill.direccion || crmActual?.direccion || null,
        barrio: prefill.barrio || crmActual?.barrio || null,
        updatedAt: serverTimestamp(),
        updatedBy: emailUsr,
        ...(huboCorreccion ? { datosCorregidos, datosCorregidosPor: emailUsr, datosCorregidosAt: serverTimestamp() } : {}),
      };
      // La nota de gestión entra una sola vez por cotización (PDF + copiar + guardar no la triplican).
      if (!mismaCotizacion) {
        const notasPrev = Array.isArray(crmActual?.notas) ? crmActual.notas : [];
        payload.notas = [...notasPrev, {
          texto: `Presupuesto generado: ${fmt(q.totalConIva)}/mes IVA inc. (propuesta ${pdfConfig.propuestaNro})`,
          autor: emailUsr,
          at: new Date().toISOString(),
        }];
      }
      if (cambiarEstado && prePropuesta) payload.estado = "propuesta";

      const libre = estaLibre(crmActual);
      let asignado = false;
      if (crmActual?.asignadoEmail) {
        // Gestión del titular (o de superadmin): empuja el reloj +60 días.
        payload.ultimaGestionAt = nowTs;
        payload.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
      } else if (libre && !isSuperAdmin) {
        // Auto-reclamo: cotizar un edificio libre lo asigna a quien cotiza.
        asignado = true;
        payload.asignadoEmail = emailLc;
        payload.asignadoA = miNombre;
        payload.asignadoAt = nowTs;
        payload.ultimaGestionAt = nowTs;
        payload.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
        payload.asignacionHistorial = [
          ...(Array.isArray(crmActual?.asignacionHistorial) ? crmActual.asignacionHistorial : []),
          { email: emailLc, name: miNombre, at: new Date().toISOString(), por: emailUsr, tipo: "asignacion" },
        ];
      }

      const ref = doc(db, "prospectos_crm", String(prefill.padron));
      const esReclamo = !isSuperAdmin && !crmActual?.asignadoEmail && libre;
      if (esReclamo) {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          const cur = snap.exists() ? snap.data() : {};
          if (!estaLibre(cur)) throw new Error("OCUPADO");
          tx.set(ref, payload, { merge: true });
        });
      } else {
        await setDoc(ref, payload, { merge: true });
      }
      updateCrm(prefill.padron, payload);
      setRegistrado({ nro: pdfConfig.propuestaNro, monto: q.totalConIva });
      showToast(
        payload.estado === "propuesta"
          ? "Presupuesto guardado — estado: Propuesta enviada."
          : asignado
            ? "Presupuesto guardado — el edificio quedó asignado a vos."
            : "Presupuesto guardado en el prospecto.",
        "success"
      );
      return true;
    } catch (e) {
      if (e?.message === "OCUPADO") {
        showToast("Otro agente tomó este prospecto recién. Refrescá para ver el estado.", "error");
      } else {
        showToast(e.message || "No se pudo guardar en el prospecto.", "error");
      }
      return false;
    }
  };

  const handleGuardarEnProspecto = async () => {
    if (savingProspecto) return;
    setSavingProspecto(true);
    await registrarEnProspecto({ cambiarEstado: marcarPropuesta && prePropuesta });
    setSavingProspecto(false);
  };

  const handlePdf = async () => {
    if (generating) return;
    setGenerating(true);
    setPdfError(false);
    try {
      const safeNombre = (pdfConfig.edificio || "Presupuesto").replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").trim();
      const filename = `Presupuesto Habitia - ${safeNombre} - ${pdfConfig.propuestaNro}.pdf`;
      await generarPresupuestoPdf(templateRef.current, filename, { download: true });
      // Cotizar para un prospecto = queda registrado y asignado a quien cotiza.
      if (prefill && !sinDatos) await registrarEnProspecto({ cambiarEstado: false });
    } catch (err) {
      console.error("Error generando PDF:", err);
      setPdfError(true);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ padding: isMobile ? spacing[4] : spacing[6], maxWidth: 1100, margin: "0 auto" }}>
      {prefill && (
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], background: colors.primaryLighter, color: colors.primary, borderRadius: radius.sm, padding: `${spacing[2]}px ${spacing[3]}px`, marginBottom: spacing[4], fontSize: typography.sizes.base, flexWrap: "wrap" }}>
          <FaRegBuilding size={13} />
          <span style={{ flex: 1, minWidth: 180 }}>
            Cotizando para {prefill.direccion || `el padrón ${prefill.padron}`} · padrón {prefill.padron}. Al descargar o copiar la cotización queda guardada en su ficha y el edificio asignado a vos; si corregís unidades o antigüedad, también se guardan.
          </span>
          <button
            type="button"
            onClick={clearPrefill}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", border: `1px solid ${colors.primaryLight}`, borderRadius: radius.sm, background: "transparent", color: colors.primary, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit", transition: "all 0.15s ease" }}
          >
            <FaTimes size={10} /> Quitar
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 400px", gap: spacing[5], alignItems: "start" }}>
        {/* ============ Columna izquierda: formulario ============ */}
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
          <div style={{ ...cardBase, padding: spacing[5] }}>
            <div style={sectionTitle}>Edificio</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: spacing[3], marginBottom: spacing[4] }}>
              <div>
                <label htmlFor="cot-edificio" style={labelStyle}>Nombre (para el PDF)</label>
                <input
                  id="cot-edificio" type="text" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
                  placeholder="Edificio Millán" value={edificio} onChange={(e) => setEdificio(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="cot-ubicacion" style={labelStyle}>Dirección</label>
                <input
                  id="cot-ubicacion" type="text" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
                  placeholder="Av. Millán 4157, Montevideo" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}
                />
              </div>
            </div>
            <Stepper id="cot-apartamentos" icon={FaRegBuilding} label="Apartamentos" hint={`Tarifa escalonada desde ${fmt(TARIFA_RAPIDA.tramosApartamentos[0].precio)} c/u IVA inc.`} value={inputs.apartamentos} onChange={(v) => setCant("apartamentos", v)} />
            <Stepper id="cot-locales" icon={FaStore} label="Locales comerciales" hint={`${fmt(TARIFA_RAPIDA.precioLocal)} c/u IVA inc.`} value={inputs.locales} onChange={(v) => setCant("locales", v)} />
            <Stepper id="cot-antiguedad" icon={FaRegClock} label="Antigüedad (años)" hint={`Más de ${TARIFA_RAPIDA.antiguedad.umbralAnios} años: +${Math.round((TARIFA_RAPIDA.antiguedad.factor - 1) * 100)}% sobre el total`} value={inputs.antiguedadAnios} onChange={(v) => setCant("antiguedadAnios", v)} />
          </div>

          <div style={{ ...cardBase, padding: spacing[5] }}>
            <div style={sectionTitle}>Servicios adicionales</div>
            <Stepper id="cot-porteros" icon={FaUserTie} label="Portero" hint={`Liquidación de sueldo · ${fmt(TARIFA_RAPIDA.adicionales.portero)} c/u IVA inc.`} value={inputs.porteros} onChange={(v) => setCant("porteros", v)} />
            <Stepper id="cot-limpiadoras" icon={FaBroom} label="Limpieza" hint={`Liquidación de sueldo · ${fmt(TARIFA_RAPIDA.adicionales.limpiadora)} c/u IVA inc.`} value={inputs.limpiadoras} onChange={(v) => setCant("limpiadoras", v)} />
            <Stepper id="cot-amenities" icon={FaSwimmingPool} label="Amenities" hint={`Gestión y reservas · ${fmt(TARIFA_RAPIDA.adicionales.amenity)} c/u IVA inc.`} value={inputs.amenities} onChange={(v) => setCant("amenities", v)} />
            <Stepper id="cot-ascensores" icon={FaArrowsAltV} label="Ascensores" hint={`Gestión de mantenimiento · ${fmt(TARIFA_RAPIDA.adicionales.ascensor)} c/u IVA inc.`} value={inputs.ascensores} onChange={(v) => setCant("ascensores", v)} />
            <YesNo id="cot-garages" icon={FaCar} label="Garage" hint={`Gestión de cocheras · ${fmt(TARIFA_RAPIDA.adicionales.garage)} tarifa única IVA inc.`} value={inputs.garages} onChange={(v) => setCant("garages", v)} />
          </div>

          <div style={{ ...cardBase, padding: spacing[5] }}>
            <label htmlFor="cot-promo" style={{ display: "flex", alignItems: "center", gap: spacing[2], cursor: "pointer", fontSize: typography.sizes.md, color: colors.gray900 }}>
              <input
                id="cot-promo" type="checkbox" checked={inputs.aplicaPromo}
                onChange={(e) => setInputs((prev) => ({ ...prev, aplicaPromo: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: colors.primary }}
              />
              Promo de bienvenida: {inputs.promoPorcentaje}% OFF los primeros {inputs.promoMeses} meses
            </label>
          </div>

          {/* Escala de tramos: la referencia visual para explicar el precio */}
          <div style={{ ...cardBase, padding: spacing[5] }}>
            <div style={sectionTitle}>Escala por volumen (precio por apartamento)</div>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing[1] }}>
              {TARIFA_RAPIDA.tramosApartamentos.map((t, i) => {
                const desde = i === 0 ? 1 : TARIFA_RAPIDA.tramosApartamentos[i - 1].hasta + 1;
                const activo = q.escala.detalle.some((d) => d.precio === t.precio);
                const rango = t.hasta === Infinity ? `${desde} en adelante` : `${desde} a ${t.hasta}`;
                return (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: `${spacing[2]}px ${spacing[3]}px`, borderRadius: radius.sm,
                    background: activo ? colors.primarySubtle : "transparent",
                    transition: "background 0.15s ease",
                  }}>
                    <span style={{ fontSize: typography.sizes.base, color: activo ? colors.primary : colors.gray600 }}>
                      Apartamentos {rango}
                    </span>
                    <span style={{ fontSize: typography.sizes.md, color: activo ? colors.primary : colors.gray600 }}>
                      {fmt(t.precio)} IVA inc.
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ============ Personalizar propuesta (catastro + contexto + prosa IA) ============ */}
          <div style={{ ...cardBase, padding: spacing[5] }}>
            <label htmlFor="cot-personalizar" style={{ display: "flex", alignItems: "center", gap: spacing[2], cursor: "pointer" }}>
              <input id="cot-personalizar" type="checkbox" checked={personalizar} onChange={(e) => setPersonalizar(e.target.checked)} style={{ width: 16, height: 16, accentColor: colors.primary }} />
              <span style={{ ...sectionTitle, marginBottom: 0 }}>Personalizar la propuesta</span>
            </label>
            <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, margin: `${spacing[1]}px 0 0 26px` }}>
              Suma una radiografía del edificio (catastro) y textos "cómo te resuelve tu edificio", para dejarle una propuesta a medida a la comisión.
            </p>

            {personalizar && (
              <div style={{ marginTop: spacing[4], display: "flex", flexDirection: "column", gap: spacing[4] }}>
                {/* Portada */}
                <div>
                  <label htmlFor="pp-cliente" style={labelStyle}>Cliente / contacto (portada)</label>
                  <input id="pp-cliente" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
                    placeholder="Comisión · Edificio Costa Brava" value={perso.clienteNombre} onChange={(e) => setP("clienteNombre", e.target.value)} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: spacing[3] }}>
                    <div>
                      <label htmlFor="pp-ciudad" style={labelStyle}>Ciudad</label>
                      <input id="pp-ciudad" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }} value={perso.ciudad} onChange={(e) => setP("ciudad", e.target.value)} placeholder="Montevideo" />
                    </div>
                    <div>
                      <label htmlFor="pp-juris" style={labelStyle}>Jurisdicción (tribunales)</label>
                      <input id="pp-juris" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }} value={perso.jurisdiccion} onChange={(e) => setP("jurisdiccion", e.target.value)} placeholder="Montevideo" />
                    </div>
                  </div>
                </div>

                {/* Radiografía del edificio (catastro + carga manual) */}
                <div>
                  <div style={{ ...labelStyle, marginBottom: spacing[2], color: colors.gray700 }}>Radiografía del edificio (catastro · editable)</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: spacing[2] }}>
                    <MiniField label="Unidades" value={perso.radUnidades} onChange={(v) => setP("radUnidades", v)} />
                    <MiniField label="Año" value={perso.radAnio} onChange={(v) => setP("radAnio", v)} />
                    <MiniField label="Plantas" value={perso.radPlantas} onChange={(v) => setP("radPlantas", v)} />
                    <MiniField label="m² edificados" value={perso.radM2} onChange={(v) => setP("radM2", v)} />
                    <MiniField label="Zona / barrio" value={perso.zona} onChange={(v) => setP("zona", v)} text />
                    <MiniField label="Régimen" value={perso.radRegimen} onChange={(v) => setP("radRegimen", v)} text />
                  </div>
                  <input className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box", marginTop: spacing[2], fontSize: typography.sizes.sm }}
                    placeholder="Nota de la radiografía (ej: datos a confirmar con la Comisión)" value={perso.notaRadiografia} onChange={(e) => setP("notaRadiografia", e.target.value)} />
                </div>

                {/* Contexto + IA */}
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3] }}>
                    <div>
                      <label style={labelStyle}>Perfil de propietarios</label>
                      <AppSelect value={perso.perfil} onChange={(v) => setP("perfil", v)} ariaLabel="Perfil de propietarios" options={PERFILES_PROPIETARIOS} />
                    </div>
                  </div>
                  <label htmlFor="pp-ctx" style={{ ...labelStyle, marginTop: spacing[3] }}>Contexto del edificio (lo que sepas: estado, obras, propietarios, particularidades)</label>
                  <textarea id="pp-ctx" className="habitia-input" rows={3} style={{ ...inputBase, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: typography.fontFamily }}
                    placeholder="Ej: edificio frente al mar, propietarios que veranean, hubo humedades en la fachada, la comisión quiere bajar la cuota…" value={perso.contexto} onChange={(e) => setP("contexto", e.target.value)} />
                  <button type="button" onClick={generarTextos} disabled={genIA}
                    style={{ ...btnPrimary, marginTop: spacing[3], opacity: genIA ? 0.7 : 1 }} className="habitia-btn-primary">
                    <FaMagic /> {genIA ? "Generando…" : (secciones.length ? "Regenerar textos con IA" : "Generar textos con IA")}
                  </button>
                </div>

                {/* Bloques editables "cómo te resuelve" */}
                {secciones.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
                    <div style={{ ...labelStyle, color: colors.gray700 }}>Cómo te resuelve tu edificio (editá cada bloque)</div>
                    {secciones.map((s, i) => (
                      <div key={i} style={{ border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[3], display: "grid", gap: spacing[2] }}>
                        <div style={{ display: "flex", gap: spacing[2], alignItems: "center" }}>
                          <input className="habitia-input" style={{ ...inputBase, flex: 1, boxSizing: "border-box" }} placeholder="Título del bloque" value={s.titulo} onChange={(e) => updSeccion(i, "titulo", e.target.value)} />
                          <button type="button" onClick={() => delSeccion(i)} aria-label="Quitar bloque" style={{ ...btnSecondary, padding: "8px 10px", color: colors.error }}><FaTrash size={12} /></button>
                        </div>
                        <textarea className="habitia-input" rows={3} style={{ ...inputBase, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: typography.fontFamily }} placeholder="Texto del bloque" value={s.cuerpo} onChange={(e) => updSeccion(i, "cuerpo", e.target.value)} />
                      </div>
                    ))}
                    <button type="button" onClick={addSeccion} style={{ ...btnSecondary, justifyContent: "center" }} className="habitia-btn-secondary"><FaPlus size={11} /> Agregar bloque</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ============ Columna derecha: resultado en vivo ============ */}
        <div style={{ ...cardBase, padding: spacing[5], position: isMobile ? "static" : "sticky", top: spacing[5] }}>
          <div style={sectionTitle}>Cotización</div>

          {sinDatos ? (
            <p style={{ fontSize: typography.sizes.md, color: colors.gray400, margin: 0 }}>
              Ingresá la cantidad de apartamentos para cotizar.
            </p>
          ) : (
            <>
              <div style={{ marginBottom: spacing[4] }}>
                <div style={{ fontSize: 32, color: colors.primary, lineHeight: 1.1 }}>
                  {fmt(q.totalConIva)} <span style={{ fontSize: typography.sizes.md, color: colors.gray600 }}>/mes IVA incluido</span>
                </div>
                <div style={{ fontSize: typography.sizes.base, color: colors.gray600, marginTop: spacing[1] }}>
                  Precios con IVA ({TARIFA_RAPIDA.ivaPorcentaje}%) incluido
                  {q.redondeo > 0 ? ` · incluye redondeo de ${fmt(q.redondeo)}` : ""}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: spacing[1], marginBottom: spacing[4] }}>
                {q.conceptos.map((c) => (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", gap: spacing[3], fontSize: typography.sizes.base }}>
                    <span style={{ color: colors.gray600 }}>{c.label} × {c.cantidad}</span>
                    <span style={{ color: colors.gray900, whiteSpace: "nowrap" }}>{fmt(c.subtotal)}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: spacing[2], marginBottom: spacing[4] }}>
                <InfoPill bg={colors.primaryLighter} color={colors.primary}>
                  {fmt(q.totalPorApartamentoConIva)} por apartamento (IVA inc.)
                </InfoPill>
                {q.ahorroVolumen > 0 && (
                  <InfoPill bg={colors.successLight} color={colors.successDark}>
                    Escala por volumen: ahorro de {fmt(q.ahorroVolumen)}/mes vs. tarifa base
                  </InfoPill>
                )}
                {q.antiguedadAplica && (
                  <InfoPill bg={colors.warningLight} color={colors.warningDark}>
                    Edificio de más de {TARIFA_RAPIDA.antiguedad.umbralAnios} años: +{Math.round((TARIFA_RAPIDA.antiguedad.factor - 1) * 100)}% ({fmt(q.recargoAntiguedad)}/mes)
                  </InfoPill>
                )}
                {q.minimoAplicado && (
                  <InfoPill bg={colors.warningLight} color={colors.warningDark}>
                    Aplica tarifa mínima de {fmt(TARIFA_RAPIDA.minimoApartamentos)} IVA inc.
                  </InfoPill>
                )}
                {q.aplicaPromo && q.promoPorcentaje > 0 && (
                  <InfoPill bg={colors.primarySubtle} color={colors.primary}>
                    Promo: primeros {q.promoMeses} meses a {fmt(q.promoTotalConIva)}/mes
                  </InfoPill>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
                <button type="button" style={{ ...btnPrimary, justifyContent: "center", opacity: generating ? 0.7 : 1 }} className="habitia-btn-primary" onClick={handlePdf} disabled={generating}>
                  <FaFilePdf /> {generating ? "Generando…" : "Descargar presupuesto PDF"}
                </button>
                <button type="button" style={{ ...btnSecondary, justifyContent: "center" }} className="habitia-btn-secondary" onClick={handleCopiar}>
                  {copiado ? <FaCheck color={colors.success} /> : <FaCopy />} {copiado ? "Copiado" : "Copiar resumen (WhatsApp)"}
                </button>
                {pdfError && (
                  <InfoPill bg={colors.errorLight} color={colors.errorDark}>
                    No se pudo generar el PDF. Probá de nuevo.
                  </InfoPill>
                )}
                {prefill && (
                  <>
                    {prePropuesta && (
                      <label htmlFor="cot-marcar-propuesta" style={{ display: "flex", alignItems: "center", gap: spacing[2], fontSize: typography.sizes.base, color: colors.gray700, cursor: "pointer", marginTop: spacing[1] }}>
                        <input
                          id="cot-marcar-propuesta" type="checkbox" checked={marcarPropuesta}
                          onChange={(e) => setMarcarPropuesta(e.target.checked)}
                          style={{ width: 15, height: 15, accentColor: colors.primary }}
                        />
                        Marcar estado como "Propuesta enviada"
                      </label>
                    )}
                    <button type="button" style={{ ...btnPrimary, justifyContent: "center", opacity: savingProspecto ? 0.7 : 1 }} className="habitia-btn-primary" onClick={handleGuardarEnProspecto} disabled={savingProspecto}>
                      <FaAddressCard /> {savingProspecto ? "Guardando…" : "Guardar en prospecto"}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Template offscreen para la captura del PDF (html2canvas necesita el DOM renderizado) */}
      <div aria-hidden="true" style={{ position: "absolute", left: -20000, top: 0 }}>
        <PresupuestoTemplate ref={templateRef} config={pdfConfig} />
      </div>
    </div>
  );
}

/** Contador con botones +/- e input numerico editable, touch-friendly para uso en la calle. */
function Stepper({ id, icon: Icon, label, hint, value, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: spacing[3], padding: `${spacing[2]}px 0` }}>
      <div style={{ width: 36, height: 36, borderRadius: radius.sm, background: colors.primaryLighter, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={15} color={colors.primary} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <label htmlFor={id} style={{ fontSize: typography.sizes.md, color: colors.gray900, display: "block" }}>{label}</label>
        {hint && <div style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>{hint}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
        <button type="button" aria-label={`Restar ${label}`} style={stepBtn} className="habitia-btn-secondary" onClick={() => onChange(value - 1)} disabled={value <= 0}>
          <FaMinus size={10} />
        </button>
        <input
          id={id} type="number" min="0" inputMode="numeric" className="habitia-input"
          style={{ ...inputBase, width: 64, textAlign: "center", padding: "8px 4px" }}
          value={value} onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" aria-label={`Sumar ${label}`} style={stepBtn} className="habitia-btn-secondary" onClick={() => onChange(value + 1)}>
          <FaPlus size={10} />
        </button>
      </div>
    </div>
  );
}

/** Fila Sí/No (mismo layout que Stepper). value 0 = No, >0 = Sí. */
function YesNo({ id, icon: Icon, label, hint, value, onChange }) {
  const on = Number(value) > 0;
  const btn = (active) => ({
    padding: "8px 18px", borderRadius: radius.sm, fontSize: typography.sizes.md,
    border: `1px solid ${active ? colors.primary : colors.gray200}`,
    background: active ? colors.primary : colors.white,
    color: active ? colors.white : colors.gray900, cursor: "pointer",
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: spacing[3], padding: `${spacing[2]}px 0` }}>
      <div style={{ width: 36, height: 36, borderRadius: radius.sm, background: colors.primaryLighter, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={15} color={colors.primary} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: typography.sizes.md, color: colors.gray900, display: "block" }}>{label}</span>
        {hint && <div style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>{hint}</div>}
      </div>
      <div id={id} role="group" aria-label={label} style={{ display: "flex", gap: spacing[1] }}>
        <button type="button" aria-pressed={!on} style={btn(!on)} onClick={() => onChange(0)}>No</button>
        <button type="button" aria-pressed={on} style={btn(on)} onClick={() => onChange(1)}>Sí</button>
      </div>
    </div>
  );
}

/** Campo compacto para la radiografía (número por defecto, o texto). */
function MiniField({ label, value, onChange, text }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 2 }}>{label}</label>
      <input
        type={text ? "text" : "number"} inputMode={text ? undefined : "numeric"} min={text ? undefined : "0"}
        className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box", padding: "8px 10px" }}
        value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
      />
    </div>
  );
}

function InfoPill({ bg, color, children }) {
  return (
    <div style={{ background: bg, color, borderRadius: radius.sm, padding: `${spacing[2]}px ${spacing[3]}px`, fontSize: typography.sizes.base }}>
      {children}
    </div>
  );
}

const sectionTitle = {
  fontSize: typography.sizes.lg,
  color: colors.black,
  marginBottom: spacing[3],
};

const labelStyle = {
  display: "block",
  fontSize: typography.sizes.base,
  color: colors.gray900,
  marginBottom: spacing[1],
};

const stepBtn = {
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: colors.white,
  color: colors.gray900,
  border: `1px solid ${colors.gray200}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  transition: "all 0.15s ease",
  padding: 0,
};
